import Vue from "vue";
import { postApi } from "@/utils/http";
import {
  getChocoPackagesFromFilter,
  mergePackages,
  getChocoPackageFilter,
  isSamePackage,
  correctMissingChocoPackages,
  stripPackages,
  removeMdcDuplicates,
} from "@/utils/mdc";

import { transformPackages } from "@/utils/packages";
import { getPackageNameVersionAndHash } from "../utils/mdc";
import { orderPackages } from "@/utils/packageOrder";
import { getReversed } from "@/utils/order";

export default {
  namespaced: true,
  state: {
    mergedPackages: {},
    mdcPackages: [],
    chocoPackages: [],
    fullyLoadedPackages: {},
    computerUID: null,
  },
  mutations: {
    selectItem(state, p) {
      for (const key of Object.keys(state.mergedPackages)) {
        for (const x of state.mergedPackages[key]) {
          if (x.selectedForAction) x.selectedForAction = false;
        }
      }

      if (p) Vue.set(p, "selectedForAction", true);
    },
    removeMergedPackage(state, p) {
      const { name, uid } = getPackageNameVersionAndHash(p);
      const index = state.mergedPackages[name + uid].findIndex((x) =>
        isSamePackage(x, p)
      );

      Vue.delete(state.mergedPackages[name + uid], index);
    },
    mergeMergedPackage(state, p) {
      const { name, uid } = getPackageNameVersionAndHash(p);
      const packages = state.mergedPackages[name + uid];
      if (packages) {
        const index = packages.findIndex((x) => isSamePackage(x, p));
        if (index !== -1) {
          const item = state.mergedPackages[name + uid][index];
          Vue.set(state.mergedPackages[name + uid], index, { ...item, ...p });
        } else state.mergedPackages[name + uid].unshift(p);
      } else Vue.set(state.mergedPackages, [name + uid], [p]);
    },
    mergeMdcPackage(state, p) {
      const index = state.mdcPackages.findIndex((x) => isSamePackage(x, p));
      if (index !== -1) {
        const item = state.mdcPackages[index];
        Vue.set(state.mdcPackages, index, { ...item, ...p });
      } else state.mdcPackages.unshift(p);
    },
    editMergedPackage(state, { p, obj }) {
      const { name, uid } = getPackageNameVersionAndHash(p);
      const index = state.mergedPackages[name + uid].findIndex((x) =>
        isSamePackage(x, p)
      );
      const item = state.mergedPackages[name + uid][index];
      if (item)
        Vue.set(state.mergedPackages[name + uid], index, { ...item, ...obj });
    },
    replaceMergedPackage(state, { p, obj }) {
      const { name, uid } = getPackageNameVersionAndHash(p);
      const index = state.mergedPackages[name + uid].findIndex((x) =>
        isSamePackage(x, p)
      );
      Vue.set(state.mergedPackages[name + uid], index, obj);
    },
    addMergedPackage(state, p) {
      const { name, uid } = getPackageNameVersionAndHash(p);
      if (!state.mergedPackages[name + uid]) {
        state.mergedPackages[name + uid] = [];
      }

      state.mergedPackages[name + uid].unshift(p);
    },
    setMergedPackageVersions(state, ps) {
      if (ps.length > 0) {
        const { name, uid } = getPackageNameVersionAndHash(ps[0]);

        Vue.set(state.mergedPackages, [name + uid], ps);
        Vue.set(state.fullyLoadedPackages, [name + uid], true);
      }
    },
    setMergedPackages(state, packages) {
      state.mergedPackages = packages;
    },

    setChocoPackages(state, chocoPackages) {
      state.chocoPackages = chocoPackages;
    },
    setMdcPackages(state, mdcPackages) {
      state.mdcPackages = mdcPackages;
    },
    mergeMdcPackages(state, mdcPackages) {
      state.mdcPackages = removeMdcDuplicates(
        [].concat.apply([], [state.mdcPackages, mdcPackages])
      );
    },

    clear(state) {
      state.mergedPackages = {};
      state.fullyLoadedPackages = {};
      state.mdcPackages = [];
      state.chocoPackages = [];
      state.computerUID = null;
    },
  },
  actions: {
    async orderMergedPackages({ dispatch, commit, state, rootState }, order) {
      const stateOrder = rootState.settings.user.computerPackages?.order;
      order = getReversed(stateOrder, order);

      dispatch(
        "settings/setUser",
        { path: "computerPackages", key: "order", value: order },
        { root: true }
      );

      return orderPackages(
        state.mergedPackages,
        order
      ).then((transformedPackages) =>
        commit("setMergedPackages", transformedPackages)
      );
    },

    async fetchMdcPackages({ commit, state, rootState }, data) {
      if (state.mdcPackages.length === 0 || data.force) {
        const mdcRes = await postApi("get/latestComputerPackages", {
          domainUID: data.domainUID,
          computerUIDs: data.computerUIDs,
        });

        if (
          rootState.settings.user &&
          rootState.settings.user.profile &&
          rootState.settings.user.profile.infobar.workingStatus
        ) {
          const p = mdcRes[0].packages.find(
            (x) => x.command.status === "working"
          );
          commit("infobar/setPackage", p, { root: true });
        } else commit("infobar/setPackage", null, { root: true });

        commit("setMdcPackages", mdcRes[0].packages);
      }
    },
    async fetchAndMerge({ dispatch, commit, state, rootState }, data) {
      await Promise.all([
        dispatch("fetchMdcPackages", data),
        dispatch("source/fetch", { domainUID: data.domainUID }, { root: true }),
        dispatch(
          "settings/getUserSetting",
          { path: "computerPackages", key: "order" },
          { root: true }
        ),
      ]);

      const filters = await getChocoPackageFilter(
        rootState.source.sources,
        state.mdcPackages
      );

      const chocoRes = await getChocoPackagesFromFilter(filters);
      const chocoPackages = stripPackages(chocoRes);

      const packages = await mergePackages(
        state.mdcPackages,
        chocoPackages,
        true
      );

      return orderPackages(
        packages,
        rootState.settings.user.computerPackages.order
      )
        .then(transformPackages)
        .then((transformedPackages) =>
          commit("setMergedPackages", transformedPackages)
        );
    },

    async searchFetchAndMerge({ dispatch, commit, state, rootState }, data) {
      await dispatch("fetchMdcPackages", data);

      const chocoRes = await getChocoPackagesFromFilter(data.filters, false);

      const chocoPackages = await correctMissingChocoPackages(
        state.mdcPackages,
        chocoRes
      );

      const packages = await mergePackages(
        chocoPackages,
        state.mdcPackages,
        true,
        true
      );

      commit(
        "setMergedPackages",
        await transformPackages(
          await orderPackages(
            packages,
            rootState.settings.user.computerPackages.order
          )
        )
      );
    },
  },

  getters: {
    selectedItem: (state) => {
      for (const key of Object.keys(state.mergedPackages)) {
        const item = state.mergedPackages[key].find((x) => x.selectedForAction);
        if (item) return item;
      }
    },
    mergedPackage: (state) => (p) => {
      try {
        const { name, uid } = getPackageNameVersionAndHash(p);
        const packages = state.mergedPackages[name + uid];
        if (packages) {
          return packages.find((x) => isSamePackage(x, p));
        }

        return null;
      } catch (err) {
        console.log("mergedPackage", err);
      }
    },
    packageGroup: (state) => ({ p, filter }) => {
      const { name, uid } = getPackageNameVersionAndHash(p);
      const packages = state.mergedPackages[name + uid];

      if (packages) {
        if (filter) return packages.filter(filter);
        else packages;
      }

      return [];
    },
  },
  modules: {},
};
